import {useEffect, useMemo} from 'react';
import {TextWithIcon} from '@legit.health/ui';
import HelpIcon from '@mui/icons-material/Help';
import {Autocomplete, Box, Button, Divider, TextField, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {BasePatient} from '../../../../types';

export default function PathologySelector({
  patient,
  selectedPathologyId,
  onSelectPathology,
  t,
  onNewCondition,
  isForClinicalTrials = false
}: {
  patient: BasePatient;
  onSelectPathology: (pathologyId: string | null) => void;
  selectedPathologyId: string | null;
  t: TFunction;
  onNewCondition: () => void;
  isForClinicalTrials?: boolean;
}) {
  const {pathologiesById} = useSharedDataContext();
  const options = useMemo(
    () =>
      patient.assignedPathologies.map(function (pathologyId) {
        const pathology = pathologiesById[pathologyId];
        if (!pathology) {
          throw new Error(`Pathology with id ${pathologyId} not found`);
        }
        return {
          id: pathology.id,
          name: pathology.name
        };
      }),
    [pathologiesById, patient]
  );

  const selectedOption = options.find((option) => `${option.id}` === selectedPathologyId);

  useEffect(
    function autoSelectPathologyForClinicalTrials() {
      const firstOption = options[0];
      if (
        isForClinicalTrials &&
        options.length === 1 &&
        selectedPathologyId === null &&
        firstOption
      ) {
        onSelectPathology(`${firstOption.id}`);
      }
    },
    [isForClinicalTrials, options, selectedPathologyId, onSelectPathology]
  );

  return (
    <Box>
      <Box mb={2}>
        <Autocomplete
          options={options}
          autoHighlight
          onChange={(_, newValue) => onSelectPathology(newValue ? `${newValue.id}` : null)}
          value={selectedOption ?? null}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => `${option.id}`}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('shared/common:selectPathology')}
              slotProps={{
                htmlInput: {...params.inputProps, readOnly: true}
              }}
            />
          )}
        />
      </Box>
      {selectedPathologyId === null && !isForClinicalTrials && (
        <>
          <TextWithIcon iconLeft={<HelpIcon color="disabled" />}>
            {t('shared/common:diagnosticReport.form.selectPathology.ifNoPathology')}
          </TextWithIcon>
          <Divider sx={{my: 2}} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => onNewCondition()}
              disableElevation
              data-testid="addNewPathologyButton"
            >
              <Typography>
                {t('shared/common:diagnosticReport.form.selectPathology.newPathology')}
              </Typography>
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
